'use client'

import { Loader2, Paperclip, Send, Square } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { AttachmentChip } from './attachment-chip'
import { Button } from './button'
import { Textarea } from './textarea'

/** One in-flight attachment shown above the textarea. The composer is
 *  agnostic about how the upload happens — the host owns the upload and
 *  toggles `progress` / `error`; the composer just renders the chip. */
export interface ComposerAttachment {
  id: string
  file: File
  progress: number
  error?: string
}

export interface AppChatComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onStop?: () => void
  /** [FACTORY-115] Opt-in Escape handler. When provided, pressing Esc inside
   *  the textarea fires it (e.g. the workspace chat's interrupt "kill switch").
   *  Omitted ⇒ Esc is ignored, so the factory composer is unchanged. */
  onEscape?: () => void
  placeholder?: string
  isStreaming?: boolean
  disabled?: boolean
  className?: string
  /** Pending attachments rendered as chips above the textarea. */
  attachments?: ComposerAttachment[]
  /** Called when a chip's ✕ is clicked. Host removes the entry. */
  onAttachmentRemove?: (id: string) => void
  /** Called when the paperclip button is clicked. Host opens a file
   *  picker (or any other source — e.g. the screen-annotator). When
   *  omitted, the paperclip is not rendered. */
  onAttachClick?: () => void
}

export type ComposerKeyAction = 'submit' | 'stop' | 'escape' | null

export interface ComposerKeyDecision {
  /** Whether the keydown's default should be prevented (e.g. Enter must not
   *  insert a newline in this single-line composer). */
  preventDefault: boolean
  action: ComposerKeyAction
}

/**
 * [FACTORY-115] Pure key-handling decision for the composer, split out so the
 * Enter/Escape branching is unit-testable without a DOM. Behavior:
 *   - Escape → 'escape' (and preventDefault) only when the host opted in via
 *     `onEscape` (`hasEscape`); otherwise ignored, leaving Esc inert.
 *   - Enter (no Shift) → always preventDefault (no newline), then 'stop' when
 *     the streaming Stop affordance is showing, else 'submit' when submittable.
 *   - Shift+Enter / any other key → no-op.
 */
export function decideComposerKey(
  key: string,
  shiftKey: boolean,
  opts: { canSubmit: boolean; showStop: boolean; hasEscape: boolean },
): ComposerKeyDecision {
  if (key === 'Escape') {
    return opts.hasEscape ? { preventDefault: true, action: 'escape' } : { preventDefault: false, action: null }
  }
  if (key !== 'Enter' || shiftKey) {
    return { preventDefault: false, action: null }
  }
  // Enter (no Shift): swallow the newline regardless, but only act when allowed.
  if (!opts.canSubmit) {
    return { preventDefault: true, action: null }
  }
  return { preventDefault: true, action: opts.showStop ? 'stop' : 'submit' }
}

const AppChatComposer = React.forwardRef<HTMLTextAreaElement, AppChatComposerProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      onStop,
      onEscape,
      placeholder,
      isStreaming,
      disabled,
      className,
      attachments,
      onAttachmentRemove,
      onAttachClick,
    },
    ref,
  ) => {
    const showStop = !!isStreaming && !value.trim() && !!onStop
    const hasAttachments = !!attachments && attachments.length > 0
    const canSubmit = !disabled && (showStop || !!value.trim() || hasAttachments)

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const { preventDefault, action } = decideComposerKey(e.key, e.shiftKey, {
        canSubmit,
        showStop,
        hasEscape: !!onEscape,
      })
      if (preventDefault) {
        e.preventDefault()
      }
      if (action === 'escape') {
        onEscape?.()
      } else if (action === 'stop') {
        onStop?.()
      } else if (action === 'submit') {
        onSubmit()
      }
    }

    const handleButtonClick = () => {
      if (showStop) {
        onStop?.()
      } else {
        onSubmit()
      }
    }

    return (
      <div className={cn('flex flex-col gap-2 p-3', className)}>
        {hasAttachments ? (
          <div className="flex flex-wrap gap-2">
            {attachments!.map((a) => (
              <AttachmentChip
                key={a.id}
                variant="pending"
                file={a.file}
                progress={a.progress}
                error={a.error}
                onRemove={onAttachmentRemove ? () => onAttachmentRemove(a.id) : undefined}
              />
            ))}
          </div>
        ) : null}
        <div className="flex items-end gap-2">
          {onAttachClick ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={onAttachClick}
              disabled={disabled}
              aria-label="Attach"
              className="shrink-0 h-11 w-11 md:h-9 md:w-9 rounded-lg">
              <Paperclip className="h-4 w-4" />
            </Button>
          ) : null}
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            disabled={disabled}
            className="resize-none min-h-[44px] md:min-h-[36px] max-h-[120px] overflow-y-auto field-sizing-content"
          />
          <Button
            type="button"
            size="icon"
            onClick={handleButtonClick}
            disabled={!canSubmit}
            aria-label={showStop ? 'Stop' : 'Send'}
            className="shrink-0 h-11 w-11 md:h-9 md:w-9 rounded-lg">
            {showStop ? (
              <Square className="h-3.5 w-3.5 fill-current" />
            ) : isStreaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    )
  },
)
AppChatComposer.displayName = 'AppChatComposer'

export { AppChatComposer }
